import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Bookmark, AlertTriangle, ArrowLeftRight, DollarSign } from 'lucide-react';
import { api } from '../lib/api';
import { formatBRL } from '../lib/format';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';

/**
 * Dialog to cancel an active reservation.
 * Asks the user what to do with the deposit:
 *   REFUND  → clears reservation data (assumes deposit refunded)
 *   RETAIN  → moves deposit to retained_reservation_deposit (vendor kept it)
 */
export default function CancelReservationDialog({ open, onOpenChange, vehicleId, reservation, onCancelled }) {
  const [action, setAction] = useState('REFUND');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) setAction('REFUND');
  }, [open]);

  const deposit = Number(reservation?.reservation_deposit || 0);
  const buyer = reservation?.reservation_buyer_name || 'Reservante';

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post(`/vehicles/${vehicleId}/cancel-reservation`, { action });
      toast.success(
        action === 'RETAIN'
          ? `Reserva cancelada. R$ ${deposit.toLocaleString('pt-BR', { minimumFractionDigits: 2 })} retidos como receita.`
          : 'Reserva cancelada.'
      );
      onOpenChange(false);
      onCancelled();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao cancelar reserva');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-600" /> Cancelar reserva?
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="rounded-lg border border-border/70 bg-muted/30 p-3 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Reservante</span>
              <span className="font-medium">{buyer}</span>
            </div>
            {deposit > 0 && (
              <div className="flex items-center justify-between mt-1">
                <span className="text-muted-foreground">Sinal recebido</span>
                <span className="font-semibold tabular">{formatBRL(deposit)}</span>
              </div>
            )}
          </div>

          {deposit > 0 ? (
            <div>
              <Label className="text-sm font-semibold mb-2 block">O que fazer com o sinal?</Label>
              <div className="space-y-2">
                <label className="flex items-start gap-2 p-3 rounded-lg border border-border/70 hover:bg-muted/40 cursor-pointer" data-testid="cancel-action-refund">
                  <input
                    type="radio"
                    checked={action === 'REFUND'}
                    onChange={() => setAction('REFUND')}
                    className="mt-0.5 accent-[hsl(var(--primary))]"
                  />
                  <div className="flex-1">
                    <div className="font-medium text-sm flex items-center gap-1.5">
                      <ArrowLeftRight className="h-4 w-4 text-blue-600" /> Devolvido ao cliente
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">Sinal foi devolvido. Nenhum lançamento gerado.</div>
                  </div>
                </label>
                <label className="flex items-start gap-2 p-3 rounded-lg border border-border/70 hover:bg-muted/40 cursor-pointer" data-testid="cancel-action-retain">
                  <input
                    type="radio"
                    checked={action === 'RETAIN'}
                    onChange={() => setAction('RETAIN')}
                    className="mt-0.5 accent-[hsl(var(--primary))]"
                  />
                  <div className="flex-1">
                    <div className="font-medium text-sm flex items-center gap-1.5">
                      <DollarSign className="h-4 w-4 text-[hsl(var(--success))]" /> Retido como receita
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">Cliente desistiu e a empresa ficou com o sinal. Será somado à receita da operação.</div>
                  </div>
                </label>
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Sem sinal a tratar. A reserva será apenas cancelada.</p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Voltar</Button>
          <Button
            variant="destructive"
            onClick={submit}
            disabled={submitting}
            data-testid="cancel-reservation-confirm"
          >
            {submitting ? 'Cancelando...' : 'Confirmar cancelamento'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
